import { observable } from 'mobx'
import { CommonContainer } from 'src/models/Common'
import { APPConfig } from 'config/appConfig'
import { IS_BROWSER } from 'src/utils/application'
import {
  getLocaleCodeFromUrl,
  i18nTranslate,
  isExpressCheckout,
} from 'src/utils'
import { isAmexCardProvider } from 'src/deps/CardDeps'
import { overlayState } from 'src/views/components/Overlay/state'

/**Q
 * Handles Paymetric (WorldPay DIEComm) iframe-based payment processing.
 *
 * Provides TokenEx-compatible aliases (tokenExFormData, tokenExValidData,
 * tokenExErrorData) so consumer components can use getPaymentContainer()
 * and access the same property names regardless of which processor is active.
 */
class PaymetricContainer extends CommonContainer {
  @observable paymetricFormData = {}
  @observable paymetricValidData = {}
  @observable paymetricErrorData = {}
  @observable paymetricSessionData = {}
  @observable paymetricSavePaymentTypeValid = false
  @observable paymetricCardValid = false
  @observable paymetricCardTouched = false
  @observable paymetricCardError = false
  @observable paymetricCardHasInput = false
  @observable paymetricCvv = ''
  @observable paymetricCvvTouched = false
  @observable paymetricCvvError = false
  @observable activeIncludeCvv = true
  @observable activeIframeText = {}
  @observable activeIsCheckoutFlow = false
  paymetricIns = null
  formSubmitCallBack = null
  paymetricRetryCount = 0
  maxRetryForPaymetric = 3

  constructor(props) {
    super(props)
  }

  /** @alias for paymetricFormData */
  get tokenExFormData() {
    return this.paymetricFormData
  }
  set tokenExFormData(value) {
    this.paymetricFormData = value
  }

  /** @alias for paymetricValidData */
  get tokenExValidData() {
    return this.paymetricValidData
  }
  set tokenExValidData(value) {
    this.paymetricValidData = value
  }

  /** @alias for paymetricErrorData */
  get tokenExErrorData() {
    return this.paymetricErrorData
  }
  set tokenExErrorData(value) {
    this.paymetricErrorData = value
  }

  /** @alias for paymetricSavePaymentTypeValid */
  get tokenExSavePaymentTypeValid() {
    return this.paymetricSavePaymentTypeValid
  }
  set tokenExSavePaymentTypeValid(value) {
    this.paymetricSavePaymentTypeValid = value
  }

  /** Mirrors tokenExContainer.tokenize() so getPaymentContainer().tokenize(cb) works on both containers. */
  tokenize = cbk => {
    return this.submitPaymetricIframe(cbk)
  }

  /** Mirrors tokenExContainer.getTokenExAuthKeyFromSFO(). */
  getTokenExAuthKeyFromSFO = async () => {
    return this.getPaymetricAccessToken()
  }

  /** Mirrors tokenExContainer.loadTokenExIframe(). */
  loadTokenExIframe = () => {
    return this.loadPaymetricIframe()
  }

  /** Mirrors tokenExContainer.loadTokenExScript(). */
  loadTokenExScript = options => {
    return this.loadPaymetricScript(options)
  }

  /**
   * Normalizes card provider from tokenized Paymetric data.
   * Prefers cardType from API, then falls back to first-six / token prefix mapping.
   *
   * @param {Object} tokenizedData
   * @returns {string}
   */
  getCardProviderFromTokenData = (tokenizedData = {}) => {
    const cardTypeFromApi = (tokenizedData?.cardType || '').toString().trim()
    if (cardTypeFromApi) {
      return cardTypeFromApi.toUpperCase()
    }

    const tokenBody = (tokenizedData?.token || '').replace(/^T/, '')
    const firstSix = (tokenizedData?.firstSix || tokenBody.slice(0, 6) || '')
      .toString()
      .trim()

    const firstDigit = firstSix.charAt(0)
    const firstTwo = firstSix.substring(0, 2)
    const firstThree = firstSix.substring(0, 3)

    // Handle cards starting with '3' (AMEX, Diners Club)
    if (firstTwo === '34' || firstTwo === '37') return 'AMEX'
    if (
      firstTwo === '36' ||
      firstTwo === '38' ||
      (firstThree >= '300' && firstThree <= '305')
    ) {
      return 'DINERS'
    }

    /**
     * Based on the ticket [CX17-12062] Test Paymetric iFrame - Jira
     * and the document Intercept_eCommerce_with_XiSecure_Integration.pdf
     * we are adding the JCB regex.
     */
    const jcbPattern = '^(353|354|355|356|357|358|3528|3529|3338)'
    if (new RegExp(jcbPattern).test(firstSix)) {
      return 'JCB'
    }

    const tokenPrefixMap = {
      4: 'VISA',
      5: 'MASTERCARD',
      6: 'DISCOVER',
    }

    return tokenPrefixMap[firstSix.charAt(0)] || ''
  }
  getPaymetricCardType = () => {
    return (
      this.paymetricValidData?.cardType ||
      this.paymetricFormData?.cardType ||
      ''
    )
      .toString()
      .trim()
  }

  getIsAmexCard = () => {
    return isAmexCardProvider(this.getPaymetricCardType())
  }

  getPaymetricCvvExpectedLength = () => {
    return this.getIsAmexCard() ? 4 : 3
  }

  getPaymetricCvvMaxLength = () => {
    return this.getPaymetricCardType()
      ? this.getPaymetricCvvExpectedLength()
      : 4
  }

  onPaymetricCvvChange = event => {
    const maxLength = this.getPaymetricCvvMaxLength()
    const value = (event?.target?.value || '')
      .replace(/\D/g, '')
      .slice(0, maxLength)
    this.paymetricCvv = value

    if (this.paymetricCvvTouched) {
      this.validatePaymetricCvv()
    }
  }

  validatePaymetricCvv = () => {
    const expectedLength = this.getPaymetricCvvExpectedLength()
    const cvvPattern = this.getPaymetricCardType()
      ? new RegExp(`^\\d{${expectedLength}}$`)
      : /^\d{3,4}$/
    const cvv = this.paymetricCvv || ''
    const isValid = cvvPattern.test(cvv)

    const cvvError = i18nTranslate(
      'validation.cardCvvErrorText',
      'Please enter valid security code'
    )
    const errorMessage = isValid ? '' : cvvError
    this.paymetricCvvError = this.paymetricCvvTouched && !isValid
    this.paymetricValidData = {
      ...this.paymetricValidData,
      isCvvValid: isValid,
    }
    return { isValid, errorMessage }
  }

  validatePaymetricCvvOnBlur = () => {
    this.paymetricCvvTouched = true
    const cvvValidation = this.validatePaymetricCvv()
    const cardSession = this.paymetricSessionData?.cardNumber

    if (
      typeof window !== 'undefined' &&
      typeof window.$XIFrame !== 'undefined' &&
      cardSession?.iframeUrl
    ) {
      window.$XIFrame.validate({
        iFrameId: 'paymetricIframe',
        targetUrl: cardSession.iframeUrl,
        autosizeheight: true,
        autosizewidth: false,
        onValidate: data => {
          this._handleCardValidateResult(data)
        },
        onCardTypeChange: data => {
          this._handleCardTypeChange(data)
        },
        onInvalidHandler: data => {
          this.paymetricCardValid = false
          this.paymetricCardError = this.paymetricCardTouched
          const cardType = this._getCardTypeFromEvent(data)
          if (cardType) {
            this._handleCardTypeChange(data)
          }
        },
      })
    }

    return cvvValidation
  }

  /**
   * Builds the XML packet for the Paymetric session request.
   *
   * @param {'cardNumber'|'cvv'|'both'} [field='both'] - Which field(s) to include.
   * @returns {string} XML packet string
   */
  buildPaymetricXmlPacket = (field = 'both', options = {}) => {
    const {
      includeCvv = true,
      iframeText = {},
      isCheckoutFlow = false,
      isFromEdit = false,
    } = options
    const paymetricModeClass = isCheckoutFlow
      ? 'pm-mode-checkout'
      : 'pm-mode-wallet'

    let paymetricContextClass = 'pm-context-other'
    if (isCheckoutFlow) {
      paymetricContextClass = isExpressCheckout()
        ? 'pm-context-express'
        : 'pm-context-standard'
    }
    const paymetricEditClass = isFromEdit ? 'pm-context-edit' : ''
    const escapeXml = value =>
      String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;')

    const cardNumberLabel = escapeXml(
      iframeText.cardNumberLabel || 'Card Number *'
    )
    const cardNumberPlaceholder = escapeXml(
      iframeText.cardNumberPlaceholder || 'Card Number'
    )
    const hostUri = `${window.location.protocol}//${document.domain}${
      window.location.port ? ':' + window.location.port : ''
    }`
    const defaultLocale = APPConfig?.getConfigFromJSON?.()?.defaultLocale || ''
    const localeCode = getLocaleCodeFromUrl({
      defaultLocale,
      isReverseType: true,
    })
    const [localeCodePart = '', countryCode = ''] = (localeCode || '').split(
      '_'
    )
    const locale = localeCodePart.toLowerCase()
    const country = countryCode.toLowerCase()

    const activeEnvironment = APPConfig?.getLocalEnv?.() || ''
    const currentDomain = document?.domain || ''
    const isLocalDomain =
      currentDomain.includes('local.') || currentDomain.includes('localhost')
    const domain = isLocalDomain ? activeEnvironment : currentDomain
    const cssUri = `https://${domain}/${country}/${locale}/storefront/client/lib/paymetric-style.css`

    const cardNumberTag =
      `<tag name='div' class='pm-field pm-field-card ${paymetricModeClass}'>` +
      `<label class='pm-card-label ${paymetricModeClass}' for='cardNumber' text='${cardNumberLabel}' />` +
      `<tboxCardNumber class='key pm-card-input ${paymetricModeClass}' mask-number='true' tokenize='true' is-numeric='true' placeholder='${cardNumberPlaceholder}' maxlength='19' />` +
      `<validationMsg class='valmsg pm-valmsg pm-card-valmsg' for='cardNumber' />` +
      `</tag>`

    const cardTypeIndicatorTag =
      `<tag name='div' class='pm-card-type-indicator'>` +
      `<cardTypeIndicator class='pm-card-indicator'>` +
      `<items>` +
      `<item for='american express' class='pm-amex-indicator' />` +
      `<item for='mastercard' class='pm-mastercard-indicator' />` +
      `<item for='visa' class='pm-visa-indicator' />` +
      `<item for='discover' class='pm-discover-indicator' />` +
      `<item for='jcb' class='pm-jcb-indicator' />` +
      `<item for='diners' class='pm-diners-indicator' />` +
      `</items>` +
      `</cardTypeIndicator>` +
      `</tag>`

    const fields = cardNumberTag + cardTypeIndicatorTag

    return (
      `<?xml version='1.0' encoding='UTF-8'?>` +
      `<merchantHtmlPacketModel xmlns='Paymetric:XiIntercept:MerchantHtmlPacketModel'>` +
      `<iFramePacket>` +
      `<hostUri>${hostUri}</hostUri>` +
      `<cssUri>${cssUri}</cssUri>` +
      `</iFramePacket>` +
      `<merchantHtml>` +
      `<htmlSection class='merchant_paycontent ${paymetricModeClass} ${paymetricContextClass} ${paymetricEditClass}'>` +
      `<xiProperties error-class='xiInputError' inline-validation='false'>` +
      `<errorTooltip class='errorTooltip' hide-duration='5000' hide-effect='fadeOut' show-duration='5000' show-effect='fadeIn' />` +
      `</xiProperties>` +
      `<cardIndicatorSection>` +
      `<tag class='PaymentDetailHeader' name='div'>Credit Card Payment</tag>` +
      fields +
      `</cardIndicatorSection>` +
      `</htmlSection>` +
      `</merchantHtml>` +
      `</merchantHtmlPacketModel>`
    )
  }

  /**
   * Fetches a Paymetric iframe access token from the backend.
   *
   * @param {'cardNumber'|'cvv'|'both'} [field='both'] - Which field(s) the iframe should render.
   * @returns {Promise<{merchantGuid, accessToken, iframeUrl}|null>}
   */
  getPaymetricAccessToken = async (field = 'both', options = {}) => {
    const {
      includeCvv = true,
      iframeText = {},
      iframeTextKey = '',
      isCheckoutFlow = false,
      isFromEdit = false,
    } = options
    if (IS_BROWSER) {
      const loadParams = {
        endPointName: 'getGpsIframeAccessToken',
        postData: {
          sessionRequestType: 1,
          merchantDevelopmentEnvironment: 'javascript',
          xmlPacket: this.buildPaymetricXmlPacket(field, {
            includeCvv,
            iframeText,
            isCheckoutFlow,
            isFromEdit,
          }),
        },
        credentials: 'include',
        isToSendLocaleInQueryParam: false,
        isToSendStoreId: true,
        isToIncludeCommomHeaders: false,
      }

      try {
        const response = await this.fetchResponse(loadParams)

        const payload = response?.payload || response
        const iframeUrl = payload?.iframeUrl || payload?.IframeUrl || ''
        const respMerchantGuid =
          payload?.merchantGuid || payload?.MerchantGuid || ''
        const accessToken = payload?.accessToken || payload?.AccessToken || ''

        if (iframeUrl && accessToken) {
          const sessionEntry = {
            merchantGuid: respMerchantGuid,
            accessToken,
            iframeUrl,
            includeCvv,
            iframeTextKey,
            isCheckoutFlow,
            isFromEdit,
          }

          if (field === 'cardNumber') {
            this.paymetricSessionData = {
              ...this.paymetricSessionData,
              cardNumber: sessionEntry,
              merchantGuid: respMerchantGuid,
              accessToken,
              iframeUrl,
            }
          } else if (field === 'cvv') {
            this.paymetricSessionData = {
              ...this.paymetricSessionData,
              cvv: sessionEntry,
            }
          } else {
            this.paymetricSessionData = {
              ...this.paymetricSessionData,
              cardNumber: sessionEntry,
              cvv: sessionEntry,
              ...sessionEntry,
            }
          }
          return sessionEntry
        } else {
          this.trackPaymetricError({
            errorReport: 'Failed to get Paymetric access token',
            errorData: response,
          })
          return null
        }
      } catch (error) {
        this.trackPaymetricError({
          errorReport: 'Error fetching Paymetric access token',
          errorData: error,
        })
        return null
      }
    }
  }

  /**
   * Injects the XIFrame third-party script into the page.
   *
   * @param {Object} [options]
   * @param {boolean} [options.isFromRetry=false]
   */
  loadPaymetricScript = options => {
    const { isFromRetry = false } = options || {}

    if (!isFromRetry) {
      this.paymetricRetryCount = 0
    }

    try {
      if (IS_BROWSER) {
        const scriptEl = document.querySelectorAll('script#paymetric')
        const paymetricSrcUrl =
          APPConfig?.getAppConfig()?.paymetric?.paymetricSrcUrl

        if (scriptEl.length === 0) {
          const script = document.createElement('script')
          script.id = 'paymetric'
          script.src = paymetricSrcUrl
          script.onerror = error => {
            this.retryPaymetricScript({
              errorReport: 'Error loading Paymetric Script',
              errorData: error,
            })
          }
          document.body.appendChild(script)
        }
      }
    } catch (e) {
      this.retryPaymetricScript({
        errorReport: 'Error loading Paymetric Script',
        errorData: e,
      })
    }
  }

  /** Removes the existing XIFrame script and retries loading up to maxRetryForPaymetric times. */
  retryPaymetricScript = error => {
    document.querySelectorAll('script#paymetric').forEach(item => {
      item.remove()
    })
    if (this.paymetricRetryCount === 0) {
      this.trackPaymetricError({
        errorReport: error?.errorReport || 'Error loading Paymetric Script',
        errorData: error?.errorData || 'Error loading Paymetric Script',
      })
    }
    this.paymetricRetryCount++
    if (this.paymetricRetryCount < this.maxRetryForPaymetric) {
      this.loadPaymetricScript({ isFromRetry: true })
    }
  }

  /**
   * Logs an error to the console and to Instana monitoring.
   *
   * @param {Object} options
   * @param {string} options.errorReport
   * @param {*} options.errorData
   */
  trackPaymetricError = options => {
    const { errorReport, errorData } = options
    console.warn(errorReport, errorData)
    if (typeof window !== 'undefined' && typeof window.ineum === 'function') {
      window.ineum('reportEvent', `Paymetric: ${errorReport}`, {
        meta: errorData,
      })
    }
  }

  /**
   * Fetches the iframe session, injects the iframe into the DOM, and hides
   * the loading overlay once the iframe has fired its onload event.
   * A 10s safety timeout hides the overlay if onload never fires.
   */
  loadPaymetricIframe = async (options = {}) => {
    const {
      includeCvv = true,
      iframeText = {},
      isCheckoutFlow = false,
      isFromEdit = false,
    } = options
    overlayState.showLoader()
    this.activeIncludeCvv = includeCvv
    this.activeIframeText = iframeText
    this.activeIsCheckoutFlow = isCheckoutFlow
    const iframeTextKey = JSON.stringify(iframeText)

    const existingCardSession = this.paymetricSessionData?.cardNumber
    const cardSession =
      existingCardSession?.iframeUrl &&
      existingCardSession?.includeCvv === includeCvv &&
      existingCardSession?.iframeTextKey === iframeTextKey &&
      existingCardSession?.isCheckoutFlow === isCheckoutFlow
        ? existingCardSession
        : await this.getPaymetricAccessToken('both', {
            includeCvv,
            iframeText,
            iframeTextKey,
            isCheckoutFlow,
            isFromEdit,
          })

    if (!cardSession) {
      this.trackPaymetricError({
        errorReport: 'Failed to get Paymetric access token',
        errorData: { cardSession },
      })
      overlayState.hideLoader()
      return
    }

    if (!IS_BROWSER) {
      overlayState.hideLoader()
      return
    }

    try {
      this.paymetricErrorData = {}
      this.paymetricCardValid = false
      this.paymetricCardTouched = false
      this.paymetricCardError = false
      this.paymetricCardHasInput = false
      this.paymetricCvv = ''
      this.paymetricCvvTouched = false
      this.paymetricCvvError = false

      const loaderSafetyTimeout = setTimeout(() => {
        overlayState.hideLoader()
      }, 10000)

      const cardContainer = document.getElementById('paymetricIframeDiv')
      if (cardContainer) {
        cardContainer.replaceChildren()
        const iframeAriaLabel = includeCvv
          ? 'Paymetric Card Number and CVV'
          : 'Paymetric Card Number'
        const cardIframe = document.createElement('iframe')
        cardIframe.id = 'paymetricIframe'
        cardIframe.src = cardSession.iframeUrl
        cardIframe.width = '100%'
        cardIframe.frameBorder = '0'
        cardIframe.scrolling = 'no'
        cardIframe.style.display = 'block'
        cardIframe.style.setProperty('width', '100%', 'important')
        cardIframe.style.setProperty('max-width', '100%', 'important')
        cardIframe.style.minWidth = '0'
        cardIframe.title = ''
        cardIframe.setAttribute('aria-label', iframeAriaLabel)
        cardIframe.onload = () => {
          clearTimeout(loaderSafetyTimeout)
          overlayState.hideLoader()
          this.initPaymetricIframe()
        }
        cardContainer.appendChild(cardIframe)
        this.paymetricIns = cardIframe
      } else {
        clearTimeout(loaderSafetyTimeout)
        overlayState.hideLoader()
      }
    } catch (e) {
      this.trackPaymetricError({
        errorReport: 'Error in Paymetric iframe setup',
        errorData: e,
      })
      overlayState.hideLoader()
    }
  }

  /** Scrolls to the payment form container so all card fields are visible. */
  scrollToPaymentForm = () => {
    const section =
      document.querySelector('#qa-checkout-payment-accordion') ||
      document.querySelector('[data-testid="qa-add-payment-container"]') ||
      document.querySelector('[data-testid="qa-edit-payment-container"]') ||
      document.querySelector('.paymetric-block-card')
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  /**
   * Updates paymetricCardValid / paymetricCardError from the $XIFrame onValidate callback.
   * Handles all observed response shapes: plain boolean, JSON string, or object.
   *
   * @param {boolean|string|Object} data - Value passed by the XIFrame onValidate callback.
   */
  _handleCardValidateResult = data => {
    let result = data
    if (typeof data === 'string') {
      // the below change is to handle error thrown while typing card number
      const normalized = data.trim().toLowerCase()
      if (normalized === 'true') {
        result = true
      } else if (normalized === 'false') {
        result = false
      } else {
        try {
          result = JSON.parse(data)
        } catch (_) {}
      }
    }

    const isValid =
      result === true ||
      result?.IsValid === true ||
      result?.HasPassed === true ||
      result?.isValid === true ||
      result?.hasPassed === true

    this.paymetricCardValid = isValid
    this.paymetricCardError = !isValid && this.paymetricCardTouched
    const cardType =
      result?.cardType ||
      result?.CardType ||
      result?.cardBrand ||
      result?.CardBrand ||
      ''
    if (cardType) {
      this.paymetricValidData = {
        ...this.paymetricValidData,
        cardType,
      }

      // Truncate CVV if the new card type has a shorter max length
      // (e.g. switching from AMEX 4-digit to Visa 3-digit via paste)
      const maxLength = this.getPaymetricCvvMaxLength()
      if ((this.paymetricCvv || '').length > maxLength) {
        this.paymetricCvv = this.paymetricCvv.slice(0, maxLength)
      }

      if (this.paymetricCvvTouched) {
        this.validatePaymetricCvv()
      }
    }
  }

  _getCardTypeFromEvent = data => {
    let result = data
    if (typeof data === 'string') {
      try {
        result = JSON.parse(data)
      } catch (_) {}
    }

    if (typeof result === 'string') {
      return result
    }

    return (
      result?.cardType ||
      result?.CardType ||
      result?.cardBrand ||
      result?.CardBrand ||
      result?.paymentType ||
      result?.PaymentType ||
      result?.value ||
      result?.Value ||
      ''
    )
  }

  _handleCardTypeChange = data => {
    const rawCardType = this._getCardTypeFromEvent(data)

    if (!rawCardType) {
      return
    }

    this.paymetricCardHasInput = true
    this.paymetricValidData = {
      ...this.paymetricValidData,
      cardType: rawCardType,
    }

    const maxLength = this.getPaymetricCvvMaxLength()
    if ((this.paymetricCvv || '').length > maxLength) {
      this.paymetricCvv = this.paymetricCvv.slice(0, maxLength)
    }

    if (this.paymetricCvvTouched) {
      this.validatePaymetricCvv()
    }

    // Re-validate the card when user edits after a submit-time error.
    if (this.paymetricCardTouched) {
      this.validatePaymetricCard()
    }
  }

  validatePaymetricCard = (options = {}) => {
    const { markTouched = false } = options
    const cardSession = this.paymetricSessionData?.cardNumber

    if (
      markTouched ||
      this.paymetricCardTouched ||
      this.paymetricCardError ||
      this.paymetricCardValid
    ) {
      this.paymetricCardTouched = true
    }

    if (
      typeof window === 'undefined' ||
      typeof window.$XIFrame === 'undefined' ||
      !cardSession?.iframeUrl
    ) {
      return false
    }

    window.$XIFrame.validate({
      iFrameId: 'paymetricIframe',
      targetUrl: cardSession.iframeUrl,
      onValidate: data => {
        if (markTouched) {
          this.paymetricCardTouched = true
        }
        this._handleCardValidateResult(data)
      },
      onCardTypeChange: data => {
        this._handleCardTypeChange(data)
      },
      onInvalidHandler: () => {
        if (markTouched) {
          this.paymetricCardTouched = true
        }
        this.paymetricCardValid = false
        this.paymetricCardError = this.paymetricCardTouched
      },
    })

    return true
  }

  /**
   * Calls $XIFrame.init() once the iframe is fully loaded.
   * inline-validation='true' in the XML packet means XIFrame natively
   * shows/hides field errors on blur inside the iframe — no explicit
   * $XIFrame.validate() call needed. onValidate / onInvalidHandler keep
   * our MobX state (paymetricCardValid, paymetricCardError) in sync.
   * Errors are only surfaced in the UI after the user has touched the
   * card field (paymetricCardTouched gate).
   */
  initPaymetricIframe = () => {
    const cardSession = this.paymetricSessionData?.cardNumber

    if (!cardSession?.iframeUrl || typeof window.$XIFrame === 'undefined') {
      this.trackPaymetricError({
        errorReport: 'Paymetric $XIFrame not ready for init',
        errorData: {
          hasCardSession: !!cardSession?.iframeUrl,
          hasXIFrame: typeof window.$XIFrame !== 'undefined',
        },
      })
      return
    }

    const initializeIframe = window.$XIFrame.onload || window.$XIFrame.init

    if (typeof initializeIframe !== 'function') {
      this.trackPaymetricError({
        errorReport: 'Paymetric $XIFrame initialize function not available',
        errorData: {
          hasOnload: typeof window.$XIFrame.onload === 'function',
          hasInit: typeof window.$XIFrame.init === 'function',
        },
      })
      return
    }

    initializeIframe({
      iFrameId: 'paymetricIframe',
      targetUrl: cardSession.iframeUrl,
      autosizeheight: true,
      autosizewidth: false,
      onValidate: data => {
        this._handleCardValidateResult(data)
      },
      onCardTypeChange: data => {
        this._handleCardTypeChange(data)
      },
      onInvalidHandler: () => {
        this.paymetricCardValid = false
        this.paymetricCardError = this.paymetricCardTouched
      },
    })

    // A cross-origin iframe takes focus away from the parent window, so:
    //   window 'blur'  fires when the user clicks INTO the card iframe
    //   window 'focus' fires when the user tabs/clicks back OUT of the iframe
    // We only call $XIFrame.validate() on the focus-out, never during typing.
    this._xiFrameHasFocus = false

    this._onWindowBlurForCard = () => {
      const paymetricIframe = document.getElementById('paymetricIframe')
      // Only mark iframe focus when the iframe itself had focus.
      if (paymetricIframe && document.activeElement === paymetricIframe) {
        this._xiFrameHasFocus = true
      }
    }

    this._onWindowFocusForCard = () => {
      if (this._xiFrameHasFocus) {
        this._xiFrameHasFocus = false
        // Skip validate entirely until user has started entering digits
        if (this.paymetricCardHasInput) {
          this.validatePaymetricCard({ markTouched: true })
        }
      }
    }

    window.addEventListener('blur', this._onWindowBlurForCard)
    window.addEventListener('focus', this._onWindowFocusForCard)
  }

  /** Updates paymetricFormData / paymetricValidData or sets paymetricErrorData on failure. */
  handlePaymetricResponse = (data = {}) => {
    if (data?.hasError) {
      const cardType = this.getPaymetricCardType()
      console.log(
        '[PAYMETRIC_DEBUG] [Paymetric Error] Payment validation failed'
      )
      this.trackPaymetricError({
        errorReport: 'Paymetric submission failed',
        errorData: data,
      })
      this.paymetricValidData = {
        ...(cardType ? { cardType } : {}),
        isValid: false,
        hasPassed: false,
      }
      this.paymetricErrorData = {
        ...this.paymetricErrorData,
        error: data?.error || 'Payment validation failed',
        errorSequence: (this.paymetricErrorData?.errorSequence || 0) + 1,
      }
    } else {
      this.paymetricErrorData = {}
      this.paymetricFormData = {
        ...data,
      }
      this.paymetricValidData = {
        ...this.paymetricValidData,
        isValid: true,
        hasPassed: data?.hasPassed === true,
      }

      if (this.formSubmitCallBack) {
        this.formSubmitCallBack()
        this.formSubmitCallBack = null
      }
    }
  }

  /**
   * Submits the card iframe via $XIFrame.submit(), then retrieves the
   * tokenized card data from GPS-tokenize.
   *
   * Only the card iframe is submitted — Worldpay links the CVV automatically
   * via the shared merchantGuid (standard DIEComm split-iframe pattern).
   *
   * @param {Function} cbk - Callback invoked after successful token retrieval.
   */
  submitPaymetricIframe = async cbk => {
    if (!this.paymetricIns || typeof window.$XIFrame === 'undefined') {
      this.trackPaymetricError({
        errorReport: 'Paymetric iframe or $XIFrame not ready for submission',
        errorData: { hasIframe: !!this.paymetricIns },
      })
      overlayState.hideLoader()
      return
    }

    this.formSubmitCallBack = cbk

    const cardSession = this.paymetricSessionData?.cardNumber
    console.log(
      '[PAYMETRIC_DEBUG] [Paymetric] Token consumed - $XIFrame.submit() called'
    )

    try {
      this.paymetricCardTouched = true
      this.paymetricCardError = false
      this.paymetricCvvTouched = true
      const isCvvValid = this.activeIncludeCvv
        ? this.validatePaymetricCvv().isValid
        : true
      if (!isCvvValid) {
        console.log(
          '[PAYMETRIC_DEBUG] [Paymetric] CVV validation failed on retry attempt'
        )
        this.handlePaymetricResponse({
          hasError: true,
          error: 'Payment validation failed',
        })
        this.scrollToPaymentForm()
        overlayState.hideLoader()
        return
      }

      const cardResult = await new Promise(resolve => {
        window.$XIFrame.submit({
          iFrameId: 'paymetricIframe',
          targetUrl: cardSession?.iframeUrl,
          onSuccess: msg => {
            try {
              const parsed = JSON.parse(msg)
              resolve(parsed)
            } catch (e) {
              resolve({ data: { HasPassed: true } })
            }
          },
          onError: error => {
            let parsed = error
            if (typeof error === 'string') {
              try {
                parsed = JSON.parse(error)
              } catch (_) {}
            }
            resolve({ data: { HasPassed: false }, _rawError: parsed })
          },
        })
      })

      if (!cardResult?.data?.HasPassed) {
        console.log(
          '[PAYMETRIC_DEBUG] [Paymetric Error] HasPassed=false - Session may be dead or card validation failed',
          {
            rawError: cardResult?._rawError,
            sessionUrl: cardSession?.iframeUrl?.slice(0, 80),
          }
        )
        this.paymetricCardValid = false
        this.paymetricCardError = true
        this.handlePaymetricResponse({
          hasError: true,
          error: 'Payment validation failed',
        })
        this.scrollToPaymentForm()
        window.$XIFrame.validate({
          iFrameId: 'paymetricIframe',
          targetUrl: cardSession?.iframeUrl,
        })
        overlayState.hideLoader()
        return
      }

      this.paymetricCardValid = true
      this.paymetricCardError = false
      await this.retrievePaymetricToken(cardSession?.accessToken)
    } catch (e) {
      console.log(
        '[PAYMETRIC_DEBUG] [Paymetric] Payment submission error, retrying session'
      )
      this.handlePaymetricResponse({
        hasError: true,
        error: e?.message || 'Payment submission failed',
      })
      overlayState.hideLoader()
      this.retryPaymetricSession()
      this.trackPaymetricError({
        errorReport: 'Error submitting Paymetric form',
        errorData: e,
      })
    }
  }

  /**
   * Retrieves the tokenized card data from GPS-tokenize after a successful iframe submit.
   *
   * @param {string} accessToken - The card session access token from the iframe-access-token response.
   */
  retrievePaymetricToken = async accessToken => {
    if (!accessToken) {
      this.trackPaymetricError({
        errorReport: 'Missing access token for Paymetric token retrieval',
        errorData: {},
      })
      return
    }

    try {
      const loadParams = {
        endPointName: 'getPaymetricToken',
        pathParams: accessToken,
        credentials: 'include',
        isToSendLocaleInQueryParam: false,
        isToSendStoreId: true,
        isToIncludeCommomHeaders: false,
      }

      const response = await this.fetchResponse(loadParams)
      const payload = response?.payload || response
      const includeCvv = this.activeIncludeCvv !== false

      const rawToken =
        payload?.token ||
        payload?.Token ||
        payload?.paymetricToken ||
        payload?.PaymetricToken ||
        ''

      if (rawToken) {
        const token = rawToken
        const lastFourFromToken = token ? token.slice(-4) : ''
        const lastFour =
          payload?.lastFour || payload?.LastFour || lastFourFromToken || ''
        const cardType = payload?.cardType || payload?.CardType || ''
        const tokenBody = token.startsWith('T') ? token.slice(1) : token
        const rawFirstSix =
          payload?.firstSix || payload?.FirstSix || tokenBody.slice(0, 6) || ''
        const firstSix = rawFirstSix ? rawFirstSix.slice(0, 6) : ''
        const kHash = payload?.kHash || payload?.KHash || payload?.khash || ''
        const referenceNumber = includeCvv
          ? payload?.referenceNumber || payload?.ReferenceNumber || ''
          : ''
        const tokenHMAC = includeCvv
          ? payload?.tokenHMAC || payload?.TokenHMAC || payload?.tokenHmac || ''
          : ''
        const cvv = includeCvv ? payload?.cvv || payload?.CVV || '' : ''
        const cvvIncluded = includeCvv
          ? typeof payload?.cvvIncluded === 'boolean'
            ? payload.cvvIncluded
            : !!(referenceNumber && tokenHMAC)
          : undefined

        this.paymetricFormData = {
          token,
          lastFour,
          cardType,
          firstSix,
          kHash,
          ...(includeCvv && referenceNumber ? { referenceNumber } : {}),
          ...(includeCvv && tokenHMAC ? { tokenHMAC } : {}),
          ...(includeCvv && this.paymetricCvv
            ? { cvv: this.paymetricCvv }
            : includeCvv && cvv
              ? { cvv }
              : {}),
          ...(includeCvv
            ? { cvvIncluded: false }
            : typeof cvvIncluded === 'boolean'
              ? { cvvIncluded }
              : {}),
          cardData: payload,
          includeCvv,
        }

        if (this.formSubmitCallBack) {
          this.formSubmitCallBack()
          this.formSubmitCallBack = null
        }
      } else {
        this.trackPaymetricError({
          errorReport: 'Failed to retrieve Paymetric token',
          errorData: response,
        })
        overlayState.hideLoader()
      }
    } catch (error) {
      this.trackPaymetricError({
        errorReport: 'Error retrieving Paymetric token',
        errorData: error,
      })
      overlayState.hideLoader()
    }
  }

  /** Clears both iframe containers and session data, then reloads fresh sessions. */
  retryPaymetricSession = async () => {
    console.log(
      '[PAYMETRIC_DEBUG] [Paymetric] Session refresh triggered - iframe will reload, user must re-enter card data'
    )
    const cardContainer = document.getElementById('paymetricIframeDiv')
    if (cardContainer) {
      cardContainer.replaceChildren()
    }

    const cvvContainer = document.getElementById('paymetricCvvIframeDiv')
    if (cvvContainer) {
      cvvContainer.replaceChildren()
    }

    this.paymetricSessionData = {}
    this.paymetricFormData = {}
    this.paymetricValidData = {}
    this.paymetricErrorData = {}
    this.paymetricCardValid = false
    this.paymetricCardTouched = false
    this.paymetricCardError = false
    this.paymetricCardHasInput = false
    this.paymetricCvv = ''
    this.paymetricCvvTouched = false
    this.paymetricCvvError = false
    await this.loadPaymetricIframe({
      includeCvv: this.activeIncludeCvv !== false,
      iframeText: this.activeIframeText || {},
      isCheckoutFlow: this.activeIsCheckoutFlow === true,
    })
  }

  /**
   * Validates that payment data is ready; scrolls to the payment section if not.
   *
   * @param {Function} cbk - Callback invoked if validation passes.
   */
  validatePaymetric = cbk => {
    if (
      this.paymetricValidData?.isValid ||
      this.paymetricValidData?.hasPassed
    ) {
      if (cbk) cbk()
    } else {
      this.scrollToPaymentForm()
    }
  }

  /** Removes event listeners and clears iframe containers and the XIFrame script on unmount. */
  cleanup = (options = {}) => {
    const { removeScript = true } = options
    if (IS_BROWSER) {
      if (this._onWindowBlurForCard) {
        window.removeEventListener('blur', this._onWindowBlurForCard)
        this._onWindowBlurForCard = null
      }
      if (this._onWindowFocusForCard) {
        window.removeEventListener('focus', this._onWindowFocusForCard)
        this._onWindowFocusForCard = null
      }
      this._xiFrameHasFocus = false
      const cardContainer = document.getElementById('paymetricIframeDiv')
      if (cardContainer) {
        cardContainer.replaceChildren()
      }

      const cvvContainer = document.getElementById('paymetricCvvIframeDiv')
      if (cvvContainer) {
        cvvContainer.replaceChildren()
      }

      if (removeScript) {
        const scriptEl = document.querySelector('script#paymetric')
        if (scriptEl) scriptEl.remove()
      }
    }
    this.paymetricSessionData = {}
    this.paymetricFormData = {}
    this.paymetricValidData = {}
    this.paymetricErrorData = {}
    this.paymetricCardValid = false
    this.paymetricCardTouched = false
    this.paymetricCardError = false
    this.paymetricCardHasInput = false
    this.paymetricCvv = ''
    this.paymetricCvvTouched = false
    this.paymetricCvvError = false
    this.formSubmitCallBack = null
    this.paymetricIns = null
    this.activeIncludeCvv = true
    this.activeIframeText = {}
    this.activeIsCheckoutFlow = false
  }
}

const paymetricContainer = new PaymetricContainer()

export { paymetricContainer }
export default paymetricContainer
